import type { Questionnaire } from '@medplum/fhirtypes';

/** Template ids that must never be shared with or assigned to clients. */
export const CLIENT_SHARE_RESTRICTED_TEMPLATE_IDS = [
  'meridian-counseling-patient-safety-plan',
  'meridian-counseling-suicide-risk-assessment',
] as const;

export const CLIENT_SHARE_RESTRICTED_TEMPLATE_CANONICAL_URLS = [
  'https://unify.health/fhir/Questionnaire/meridian-counseling-patient-safety-plan',
  'https://unify.health/fhir/Questionnaire/meridian-counseling-suicide-risk-assessment',
] as const;

const RESTRICTED_ID_SET = new Set<string>(CLIENT_SHARE_RESTRICTED_TEMPLATE_IDS);
const RESTRICTED_URL_SET = new Set<string>(CLIENT_SHARE_RESTRICTED_TEMPLATE_CANONICAL_URLS);

export type ClientShareRestrictedTemplateRef = {
  id?: string;
  name?: string;
  url?: string;
  canonicalUrl?: string;
  title?: string;
  /** Template library option id (FilterMultiSelect value). */
  value?: string;
};

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

function matchesRestrictedSlug(slug: string): boolean {
  const key = normalizeKey(slug);
  return [...RESTRICTED_ID_SET].some((id) => key === id || key.includes(id));
}

/** True when the template is the Patient Safety Plan or Suicide Risk Assessment. */
export function isClientShareRestrictedTemplate(
  template: Questionnaire | ClientShareRestrictedTemplateRef | string | null | undefined,
): boolean {
  if (!template) return false;

  if (typeof template === 'string') {
    const trimmed = template.trim();
    if (RESTRICTED_ID_SET.has(trimmed)) return true;
    if (RESTRICTED_URL_SET.has(trimmed)) return true;
    return matchesRestrictedSlug(trimmed);
  }

  if (template.id && RESTRICTED_ID_SET.has(template.id)) return true;
  if ('value' in template && template.value && RESTRICTED_ID_SET.has(template.value)) return true;

  const canonical =
    ('canonicalUrl' in template && template.canonicalUrl) ? template.canonicalUrl : template.url;
  if (canonical && RESTRICTED_URL_SET.has(canonical)) return true;

  if (template.name && matchesRestrictedSlug(template.name)) return true;

  const urlSlug = template.url?.split('/').pop();
  if (urlSlug && matchesRestrictedSlug(urlSlug)) return true;

  if (template.title) {
    const title = normalizeKey(template.title);
    if (title.includes('patient safety plan') || title.includes('suicide risk assessment')) {
      return true;
    }
  }

  return false;
}

export function filterClientAssignableTemplates<T extends Questionnaire | ClientShareRestrictedTemplateRef>(
  templates: readonly T[],
): T[] {
  return templates.filter((template) => !isClientShareRestrictedTemplate(template));
}
